import { Car, supabase, getCarGradient } from '../lib/supabase';
import { Plus, Car as CarIcon, Settings as SettingsIcon } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { CompactCarCard } from '../components/cars/CompactCarCard';
import { NavigationBar } from '../components/ui/NavigationBar';
import { useNavigate } from 'react-router-dom';
import { useTranslations } from '../i18n';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { dataManager } from '../lib/dataManager';
import { useScrollRestoration } from '../hooks/useScrollRestoration';
import { usePreferences } from '../contexts/PreferencesContext';
import { getCarLimitStatus } from '../lib/carLimits';
import { AlertModal } from '../components/ui/AlertModal';

import { theme, styles } from '../lib/theme';

type DashboardProps = {
  cars: Car[];
  favoriteCars?: Car[];
  sharedCars?: Car[];
  onCarClick: (car: Car) => void;
  onAddCar: () => void;
  isDemo?: boolean;
};

type CarWithStats = Car & {
  totalExpenses: number;
  overallResult?: number | null;
  expenseCount: number;
  latestMileage: number | null;
  estimatedMileage: number | null;
  nextReminder?: { name: string; date: string; action_required: boolean }[] | null;
};

export function Dashboard({ cars, favoriteCars = [], sharedCars = [], onCarClick, onAddCar, isDemo = false }: DashboardProps) {
  const [carsWithStats, setCarsWithStats] = useState<CarWithStats[]>([]);
  const [carPreferences, setCarPreferences] = useState<Record<string, any>>({});
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { preferences } = usePreferences();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [limitAlertOpen, setLimitAlertOpen] = useState(false);

  // Restore scroll position
  useScrollRestoration(scrollContainerRef);

  // Local state for sorted cars to handle DnD smoothly
  const [sortedMyCars, setSortedMyCars] = useState<CarWithStats[]>([]);

  useEffect(() => {
    const allCars = [...cars, ...favoriteCars, ...sharedCars];
    const uniqueCars = Array.from(new Map(allCars.map(item => [item.id, item])).values());

    const initial = uniqueCars.map(c => ({
      ...c,
      totalExpenses: 0,
      expenseCount: 0,
      latestMileage: null,
      estimatedMileage: null,
      nextReminder: null
    }));

    setCarsWithStats(initial);

    // Set initial sorting for "My Vehicles" (excluding archived)
    const myCarsSorted = initial
      .filter(c => cars.some(my => my.id === c.id) && c.is_archived !== true)
      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    setSortedMyCars(myCarsSorted);
    if (!isDemo) {
      loadCarStats(uniqueCars);
      loadCarPreferences(uniqueCars);
    }
  }, [cars, favoriteCars, sharedCars, isDemo]);

  const onDragEnd = async (result: any) => {
    if (!result.destination) return;

    const items = Array.from(sortedMyCars);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setSortedMyCars(items);

    // Save to database
    const user = (await supabase.auth.getUser()).data.user;
    if (user) {
      await dataManager.reorderCars(user.id, items.map(i => i.id));
    }
  };

  const loadCarStats = async (targetCars: Car[]) => {
    const carIds = targetCars.map(c => c.id);
    if (carIds.length === 0) return;

    // Bulk Fetch Data
    const [expensesResult, mileageResult, remindersResult, favCountsResult] = await Promise.all([
      supabase.from('expenses')
        .select('car_id, amount, type')
        .in('car_id', carIds),
      supabase.from('log_entries')
        .select('car_id, mileage, date')
        .in('car_id', carIds)
        .gt('mileage', 0)
        .order('date', { ascending: false }),
      supabase.from('reminders')
        .select('car_id, name, description, remind_date, last_notified_at, action_required, note_id, notes(title, content)')
        .in('car_id', carIds)
        .eq('is_active', true)
        .eq('is_seen', false)
        .eq('action_required', true) // Only triggered ones per user request
        .order('remind_date', { ascending: true }),
      supabase.rpc('get_cars_favorite_counts', { p_car_ids: carIds })
    ]);

    const expensesByCar = (expensesResult.data || []).reduce((acc: Record<string, any[]>, e) => {
      if (!acc[e.car_id]) acc[e.car_id] = [];
      acc[e.car_id].push(e);
      return acc;
    }, {});

    const mileageByCar = (mileageResult.data || []).reduce((acc: Record<string, any[]>, m) => {
      if (!acc[m.car_id]) acc[m.car_id] = [];
      acc[m.car_id].push(m);
      return acc;
    }, {});

    const remindersByCar = (remindersResult.data || []).reduce((acc: Record<string, any[]>, r) => {
      if (!acc[r.car_id]) acc[r.car_id] = [];
      acc[r.car_id].push(r);
      return acc;
    }, {});

    const favCountsMap: Record<string, number> = {};
    if (favCountsResult.data) {
      favCountsResult.data.forEach((fc: any) => {
        favCountsMap[fc.car_id] = fc.favorite_count;
      });
    }

    const { estimateCurrentMileage } = await import('../lib/mileageUtils');

    const finalStats = targetCars.map(car => {
      const carExpenses = expensesByCar[car.id] || [];
      const totalExpenses = carExpenses.reduce((sum, e) => {
        if (!e.amount || e.type === 'info') return sum;
        return sum + parseFloat(e.amount.toString());
      }, 0);

      const totalOtherExpenses = carExpenses.reduce((sum, e) => {
        if (!e.amount || e.type === 'info' || e.type === 'fuel' || e.type === 'electricity') return sum;
        return sum + parseFloat(e.amount.toString());
      }, 0);

      const overallResult = car.is_archived 
        ? (car.sell_price || 0) - (car.purchase_price || 0) - totalOtherExpenses
        : null;

      const expenseCount = carExpenses.length;
      const mileageLogs = mileageByCar[car.id] || [];
      const latestMileage = mileageLogs.length > 0 ? mileageLogs[0].mileage : null;
      
      let estimatedMileage = null;
      if (latestMileage) {
        estimatedMileage = estimateCurrentMileage(car, mileageLogs);
      }

      const nextReminder = (remindersByCar[car.id] || []).map((r: any) => {
        let displayName = r.name;
        let displayDesc = r.description;
        let noteTitle = null;
        
        if (r.note_id && r.notes) {
          const noteContent = (r.notes as any).content || '';
          const lines = noteContent.split('\n');
          const firstLine = lines[0] || '';
          noteTitle = firstLine.replace(/[#*_`~]/g, '').trim().substring(0, 100) || null;
        }

        if (!displayName && noteTitle) {
          displayName = t.reminders.noteReminder || 'Note Reminder';
          displayDesc = noteTitle;
        } else if (displayName && !displayDesc && noteTitle) {
          displayDesc = noteTitle;
        }

        return {
          name: displayName || (r.note_id ? t.reminders.noteReminder : t.reminders.reminder),
          description: displayDesc,
          date: r.remind_date,
          action_required: r.action_required
        };
      });

      return { 
        ...car, 
        totalExpenses, 
        overallResult, 
        expenseCount, 
        latestMileage, 
        estimatedMileage, 
        nextReminder: nextReminder.length > 0 ? nextReminder : null,
        favorite_count: favCountsMap[car.id] || 0
      } as CarWithStats;
    });

    setCarsWithStats(finalStats);
    setSortedMyCars(prev => prev.map(p => {
      const updated = finalStats.find(s => s.id === p.id);
      return updated ? { ...p, ...updated } : p;
    }));
  };

  const loadCarPreferences = async (targetCars: Car[]) => {
    const userIds = Array.from(new Set(targetCars.map(c => c.user_id)));
    try {
      const prefs = await Promise.all(userIds.map(id => dataManager.fetchUserPreferences(id)));
      const prefsMap: Record<string, any> = {};
      userIds.forEach((id, index) => {
        prefsMap[id] = prefs[index];
      });
      setCarPreferences(prefsMap);
    } catch (err) {
      console.error('Error loading car preferences for dashboard:', err);
    }
  };

  // Car limit check
  const limitStatus = getCarLimitStatus(cars, preferences);

  const handleAddCarClick = () => {
    if (limitStatus.isReached) {
      setLimitAlertOpen(true);
    } else {
      onAddCar();
    }
  };

  const planLabel = preferences?.plan_type === 'pro'
    ? (t.common.planProTier || 'Pro')
    : (t.common.planFreeTier || 'Free');

  const limitAlertMessage = (t.cars.carLimitReachedMessage || '')
    .replace('{plan}', planLabel)
    .replace('{limit}', String(limitStatus.limit))
    .replace('{current}', String(limitStatus.current));

  return (
    <div className="fixed inset-0 flex flex-col h-full bg-transparent overflow-hidden">
      <div className="flex-shrink-0 z-30">
        <NavigationBar
          title={t.cars.myVehicles}
          fixed={false}
          onBack={undefined}
          leftAction={cars.length === 0 && favoriteCars.length === 0 && sharedCars.length === 0 ? {
            icon: SettingsIcon,
            onClick: () => navigate('/settings')
          } : undefined}
          rightAction={{
            icon: Plus,
            onClick: handleAddCarClick
          }}
        />
      </div>

      <div
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto w-full"
      >
        <div className={`${theme.spacing.pagePadding} pt-4 pb-32`}>
          {cars.length === 0 && favoriteCars.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className={`${theme.colors.cardBg} w-32 h-32 rounded-[3rem] shadow-xl dark:shadow-none border ${theme.colors.border} flex items-center justify-center mb-8 relative`}>
                <CarIcon size={48} className="text-blue-600 dark:text-blue-400 relative z-10" />
              </div>
              <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-3 tracking-tight">{t.cars.noCars}</h2>
              <p className="text-gray-500 font-medium max-w-[240px] leading-relaxed mb-10">{t.cars.addFirstCarNote}</p>
              <button
                onClick={handleAddCarClick}
                className={`flex items-center gap-2 text-blue-600 dark:text-blue-400 font-black text-sm uppercase tracking-widest ${theme.colors.primaryLight} px-8 py-4 rounded-2xl hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-all active:scale-95 shadow-sm shadow-blue-100 dark:shadow-none`}
              >
                <span>{t.cars.addVehicle}</span>
                <Plus size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-8 w-full">
              {sortedMyCars.length > 0 && (
                <div className="space-y-4 w-full">
                  {cars.filter(c => c.is_archived !== true).length === 0 && favoriteCars.length > 0 && (
                    <h3 className={`${theme.typography.sectionTitle} px-2`}>{t.cars.myVehicles}</h3>
                  )}
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="cars-list">
                      {(provided) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className={`${styles.card} divide-y ${theme.colors.divider} w-full`}
                        >
                          {sortedMyCars.map((car, index) => (
                            <Draggable key={car.id} draggableId={car.id} index={index}>
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  className={`w-full ${snapshot.isDragging ? `shadow-2xl z-50 ${theme.colors.cardBg} relative ring-2 ring-blue-500/20` : ''}`}
                                >
                                  <CompactCarCard
                                    car={car}
                                    gradient={getCarGradient(car.placeholder_hue)}
                                    image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                                    onClick={() => onCarClick(car)}
                                    dragHandleProps={sortedMyCars.length > 1 ? provided.dragHandleProps : undefined}
                                    preferencesOverride={carPreferences[car.user_id]}
                                    starOnRight={true}
                                  />
                                </div>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                </div>
              )}

              {/* Shared With Me section */}
              {sharedCars.filter(c => c.is_archived !== true).length > 0 && (
                <div className="space-y-4">
                  <h3 className={`${theme.typography.sectionTitle} px-2`}>{t.cars.sharedWithMe || 'Shared with Me'}</h3>
                  <div className={`${styles.card} divide-y ${theme.colors.divider}`}>
                    {sharedCars
                      .filter(sharedCar => !cars.some(my => my.id === sharedCar.id) && sharedCar.is_archived !== true)
                      .map((car) => (
                        <CompactCarCard
                          key={`shared-${car.id}`}
                          car={carsWithStats.find(c => c.id === car.id) || { ...car, totalExpenses: 0, expenseCount: 0, latestMileage: null, estimatedMileage: null }}
                          gradient={getCarGradient(car.placeholder_hue)}
                          image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                          onClick={() => onCarClick(car)}
                          preferencesOverride={carPreferences[car.user_id]}
                          starOnRight={true}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Favorites section */}
              {carsWithStats.some(c => 
                favoriteCars.some(fav => fav.id === c.id) && 
                !cars.some(my => my.id === c.id) && 
                !sharedCars.some(shared => shared.id === c.id) &&
                c.is_archived !== true
              ) && (
                <div className="space-y-4">
                  <h3 className={`${theme.typography.sectionTitle} px-2`}>{t.cars.favorites}</h3>
                  <div className={`${styles.card} divide-y ${theme.colors.divider}`}>
                    {carsWithStats
                      .filter(c => 
                        favoriteCars.some(fav => fav.id === c.id) && 
                        !cars.some(my => my.id === c.id) && 
                        !sharedCars.some(shared => shared.id === c.id) &&
                        c.is_archived !== true
                      )
                      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                      .map((car) => (
                        <CompactCarCard
                          key={`fav-${car.id}`}
                          car={car}
                          gradient={getCarGradient(car.placeholder_hue)}
                          image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                          onClick={() => onCarClick(car)}
                          preferencesOverride={carPreferences[car.user_id]}
                          starOnRight={true}
                        />
                      ))}
                  </div>
                </div>
              )}

              {/* Archived section */}
              {carsWithStats.some(c => c.is_archived) && (
                <div className="space-y-4 opacity-75">
                  <h3 className={`${theme.typography.sectionTitle} px-2`}>{(t.cars as any).archivedCars || 'Archived'}</h3>
                  <div className={`${styles.card} divide-y ${theme.colors.divider}`}>
                    {carsWithStats
                      .filter(c => c.is_archived)
                      .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
                      .map((car) => (
                        <div key={`archived-${car.id}`} className="relative opacity-60 hover:opacity-100 transition-opacity">
                          <CompactCarCard
                            car={car}
                            gradient={getCarGradient(car.placeholder_hue)}
                            image={car.use_image_icon && car.image_url ? car.image_url : undefined}
                            onClick={() => onCarClick(car)}
                            preferencesOverride={carPreferences[car.user_id]}
                            hideFavoriteIcon={true}
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <AlertModal
        isOpen={limitAlertOpen}
        title={t.cars.carLimitReachedTitle || 'Car Limit Reached'}
        message={limitAlertMessage}
        confirmLabel={t.common.done}
        onClose={() => setLimitAlertOpen(false)}
        secondaryAction={{
          label: t.cars.carLimitGoToSettings || 'Go to Settings',
          onClick: () => { navigate('/settings'); },
        }}
      />
    </div>
  );
}
